import type { MvcLeadGrid } from "@repo/models";

import { faMap } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TLPGrid } from "@repo/baseCloud/components";
import { dateOf, fullAddress, hasValue, name } from "@repo/helpers";
import { Button, ButtonColor, filterPositionsWithinSquare, MapChart } from "@repo/ui";
import { Column } from "@repo/ui/grid/models/column";
import { DateColumn } from "@repo/ui/grid/models/dateColumn";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { BreadcrumbsController } from "../../api/breadcrumbsController";
import { controller, LeadsController, LeadsFilter } from "../../api/leadsController";
import { QUERY_KEYS } from "../../api/queries";
import { MapPins } from "../../constants/mapPins";
import { MarkerMap } from "../maps/map";

const COLUMNS = [
	new Column("Lead Id", "id"),
	new DateColumn("Created On", "createdOn"),
	new Column("Rep Name").withFormat((v: MvcLeadGrid) => name(v.repFirstName, v.repLastName)),
	new Column("Rep Login", "repLogin"),
	new Column("Office", "branch"),
	new Column("Channel", "channel"),
	new Column("State", "state"),
	new Column("Converted").withFormat((i: MvcLeadGrid) =>
		i.latestEnrollment === null ? (
			"No"
		) : (
			<a href={`/MVC/EnrollmentMaster/ResiDtd?enrollmentId=${i.latestEnrollment.enrollmentId}`} target="_blank">
				{i.latestEnrollment.enrollmentId}
			</a>
		),
	),
	new Column("Disposition").withFormat((v: MvcLeadGrid) => v.latestEnrollment?.tpvDisposition ?? ""),
	new Column("Date of Sale").withFormat((i) => dateOf(i.latestEnrollment?.dateOfSale)),
	new Column("Customer Name").withFormat((v: MvcLeadGrid) =>
		name(v.latestEnrollment?.customerFirstName ?? "", v.latestEnrollment?.customerLastName ?? ""),
	),
	new Column("Customer Address").withFormat((v: MvcLeadGrid) =>
		fullAddress(
			v.latestEnrollment?.customerAddress.address1 ?? "",
			v.latestEnrollment?.customerAddress.address2 ?? "",
			v.latestEnrollment?.customerAddress.city ?? "",
			v.latestEnrollment?.customerAddress.state ?? "",
			v.latestEnrollment?.customerAddress.zip ?? "",
		),
	),
	new Column("Product").withFormat((v: MvcLeadGrid) =>
		v.latestEnrollment?.productOffer == null
			? ""
			: `${v.latestEnrollment?.productOffer.name} (${v.latestEnrollment?.productOffer.description})`,
	),
	new Column("Location").withFormat((v: MvcLeadGrid) => {
		const name = v.locationName ?? "";
		return name + (v.locationCode ? ` (${v.locationCode})` : "");
	}),
	new Column("Partner", "partnerName"),
	new Column("Machine Name", "machineName"),
] as Column<MvcLeadGrid>[];

interface Props {
	filters: LeadsFilter;
	setFilters: (filters: LeadsFilter) => void;
}

export function LeadsGrid(props: Props) {
	const [expanded, setExpanded] = useState<number | null>(null);

	const leadsQuery = useQuery({
		queryKey: [QUERY_KEYS.Leads.Leads, props.filters],
		queryFn: () => LeadsController.getLeads(props.filters),
	});

	const leads = useMemo(() => leadsQuery.data?.items ?? [], [leadsQuery.data]);

	const retailLocationsQuery = useQuery({
		queryKey: [QUERY_KEYS.Leads.RetailLocations, "999999"],
		queryFn: () => BreadcrumbsController.getRetailLocations(undefined, undefined, undefined, undefined, "999999"),
	});

	const handleRowClick = useCallback(
		(id: number) => {
			if (expanded === id) setExpanded(null);
			else setExpanded(id);
		},
		[expanded],
	);

	const columns = useMemo(
		() => [
			new Column("Map", "id").withFormat((v) => (
				<Button color={ButtonColor.Green} onClick={() => handleRowClick(v)}>
					<FontAwesomeIcon icon={faMap} color="white" />
				</Button>
			)),
			...COLUMNS,
		],
		[handleRowClick],
	);

	const getExpandedDisplay = useCallback(
		(id: number) => {
			if (id !== expanded) return <></>;
			if (!hasValue(expanded)) return <></>;

			const lead = leads.find((l) => l.id === id);

			if (!lead) return <></>;

			const filteredPositions = filterPositionsWithinSquare(
				lead.lat,
				lead.lng,
				retailLocationsQuery.data.map((item) => ({
					lat: item.lat,
					lon: item.lng,
					...item,
				})),
			);

			return (
				<div className="h-[400px]">
					<MapChart
						data={[
							...filteredPositions.map((item) => ({ ...MarkerMap.retailLocation(item), ordinal: 3 })),
							{
								id: lead.id,
								datetime: new Date(),
								lat: lead.lat ?? 0,
								lon: lead.lng ?? 0,
								tooltip: `${lead.repLogin} - ${name(lead.repFirstName, lead.repLastName)}`,
								label: [
									{
										repName: name(lead.repFirstName, lead.repLastName),
										repLogin: lead.repLogin,
										machineName: lead.machineName,
									},
								],
								image: MapPins.pinRed,
								scale: 2,
								ordinal: 1,
							},
							lead.location != null
								? {
										id: lead.location.locationId,
										datetime: new Date(),
										lat: lead.location.lat ?? 0,
										lon: lead.location.lon ?? 0,
										tooltip: `${lead.location.name} (${lead.location.locationCode})`,
										label: [
											{
												name: lead.location.name,
												code: lead.location.locationCode,
												address: fullAddress(
													lead.location.address1,
													lead.location.address2,
													lead.location.city,
													lead.location.stateName,
													lead.location.zip,
												),
											},
										],
										image: MapPins.pinGreen,
										scale: 1.5,
										ordinal: 2,
									}
								: undefined,
						].filter(Boolean)}
						type="map"
						mapType="satellite"
						id={`lead-map-${id}`}
					/>
				</div>
			);
		},
		[expanded, leads, retailLocationsQuery.data],
	);

	return (
		<TLPGrid<MvcLeadGrid, LeadsFilter>
			id="leadsGrid"
			title="Leads"
			keyProperty="id"
			filters={props.filters}
			setFilters={props.setFilters}
			query={leadsQuery}
			columns={columns}
			pageSizes={[25, 50, 100, 500]}
			expandedDisplay={getExpandedDisplay}
			apiRoute={controller}
			exportFunc={LeadsController.getLeads}
		/>
	);
}
